/**
 * CEO Command Center v11 - Space Invite Components
 *
 * Components for generating and joining sync spaces:
 * - SpaceInviteDialog: Generate invite links
 * - JoinSpaceDialog: Accept invite links
 *
 * @module components/common/SpaceInvite
 * @version 11.0.0
 */

import React, { useState, useCallback } from 'react';
import { useSyncStore } from '@/stores/syncStore';

// ============================================
// SPACE INVITE DIALOG
// ============================================

interface SpaceInviteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  spaceId: string;
  spaceName: string;
}

export const SpaceInviteDialog: React.FC<SpaceInviteDialogProps> = ({
  isOpen,
  onClose,
  spaceId,
  spaceName,
}) => {
  const { generateInvite } = useSyncStore();
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleGenerateInvite = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const url = await generateInvite(spaceId);
      setInviteUrl(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate invite');
    } finally {
      setLoading(false);
    }
  }, [spaceId, generateInvite]);

  const handleCopy = useCallback(async () => {
    if (inviteUrl) {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [inviteUrl]);

  const handleClose = () => {
    setInviteUrl(null);
    setError(null);
    setCopied(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />

      {/* Dialog */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Invite to Space</h2>
        <p className="text-sm text-gray-500 mb-4">
          Generate an invite link to share <strong>"{spaceName}"</strong> with others.
        </p>

        {!inviteUrl ? (
          <>
            <div className="p-4 bg-blue-50 rounded-lg mb-4">
              <h3 className="font-medium text-blue-900 mb-1">What gets shared?</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>- Access to sync data in this space</li>
                <li>- Encryption keys for the space</li>
                <li>- Your name as the inviter</li>
              </ul>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateInvite}
                disabled={loading}
                className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Generating...' : 'Generate Invite'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Invite Link</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={inviteUrl}
                  className="flex-1 px-3 py-2 bg-gray-50 border rounded-lg text-sm text-gray-600 font-mono truncate"
                />
                <button
                  onClick={handleCopy}
                  className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors whitespace-nowrap"
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            <div className="p-4 bg-amber-50 rounded-lg mb-4">
              <h3 className="font-medium text-amber-900 mb-1">Important</h3>
              <ul className="text-sm text-amber-800 space-y-1">
                <li>- This invite expires in 7 days</li>
                <li>- Anyone with this link can join the space</li>
                <li>- Share securely (encrypted chat, in-person)</li>
              </ul>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================
// JOIN SPACE DIALOG
// ============================================

interface JoinSpaceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const JoinSpaceDialog: React.FC<JoinSpaceDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { joinSpace, identity } = useSyncStore();
  const [inviteUrl, setInviteUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{
    spaceName: string;
    inviterName: string;
    tables: string[];
  } | null>(null);

  const handlePreview = useCallback(() => {
    setError(null);
    setPreview(null);

    try {
      const match = inviteUrl.match(/ceocc:\/\/join\/(.+)/);
      if (!match) {
        setError('Invalid invite URL format. It should start with ceocc://join/');
        return;
      }

      const inviteBase64 = match[1];
      const inviteJson = atob(inviteBase64);
      const invite = JSON.parse(inviteJson);

      if (invite.expiresAt < Date.now()) {
        setError('This invite has expired');
        return;
      }

      setPreview({
        spaceName: invite.spaceName,
        inviterName: invite.inviterName,
        tables: invite.tables,
      });
    } catch {
      setError('Could not parse invite URL. Make sure you copied the entire link.');
    }
  }, [inviteUrl]);

  const handleJoin = useCallback(async () => {
    if (!inviteUrl) return;

    setLoading(true);
    setError(null);

    try {
      await joinSpace(inviteUrl);
      onSuccess?.();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join space');
    } finally {
      setLoading(false);
    }
  }, [inviteUrl, joinSpace, onSuccess]);

  const handleClose = () => {
    setInviteUrl('');
    setError(null);
    setPreview(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />

      {/* Dialog */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Join a Space</h2>
        <p className="text-sm text-gray-500 mb-4">
          Paste an invite link to join a shared space.
        </p>

        {!identity && (
          <div className="mb-4 p-3 bg-amber-50 text-amber-700 text-sm rounded-lg">
            You need to create an identity first before joining a space.
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Invite Link</label>
          <textarea
            value={inviteUrl}
            onChange={(e) => {
              setInviteUrl(e.target.value);
              setPreview(null);
            }}
            placeholder="ceocc://join/..."
            rows={3}
            className="w-full px-3 py-2 border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>
        )}

        {preview && (
          <div className="mb-4 p-4 bg-green-50 rounded-lg">
            <h3 className="font-medium text-green-900 mb-2">Space Preview</h3>
            <div className="space-y-1 text-sm text-green-800">
              <p>
                <span className="text-green-600">Name:</span> {preview.spaceName}
              </p>
              <p>
                <span className="text-green-600">Invited by:</span> {preview.inviterName}
              </p>
              <p>
                <span className="text-green-600">Syncs:</span> {preview.tables.join(', ')}
              </p>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          {!preview ? (
            <button
              onClick={handlePreview}
              disabled={!inviteUrl.trim() || !identity}
              className="px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              Preview
            </button>
          ) : (
            <button
              onClick={handleJoin}
              disabled={loading || !identity}
              className="px-4 py-2 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Joining...' : 'Join Space'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default { SpaceInviteDialog, JoinSpaceDialog };
